import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { GetActionQuery, GetActionsQuery, GetActionMessageQuery } from './action-query.types';
import { Actions, OutstandingActionsCount } from 'app/shared/types/actions.types';
import { Action } from 'app/shared/types/action.types';
import { ActionType } from 'app/shared/types/action-type.types';
import { EmailLog } from 'app/shared/types/email-log.types';
import { ContextType } from 'app/shared/enums/context-type';

@Injectable({ providedIn: 'root' })
export class ActionQueryService {
    private readonly _http = inject(HttpClient);

    getActions(query: GetActionsQuery): Observable<Actions> {
        let params = new HttpParams();
        if (query.page) params = params.set('page', query.page.toString());
        if (query.pageSize) params = params.set('pageSize', query.pageSize.toString());
        if (query.searchTerm?.trim()) params = params.set('searchTerm', query.searchTerm.trim());
        if (query.typeId) params = params.set('typeId', query.typeId.toString());
        if (query.userId) params = params.set('userId', query.userId.toString());
        if (query.statusFilter) params = params.set('statusFilter', query.statusFilter);
        if (query.startDueDate) params = params.set('startDueDate', query.startDueDate);
        if (query.endDueDate) params = params.set('endDueDate', query.endDueDate);

        if (query.contextType && query.contextId) {
            return this._http.get<Actions>(
                `api/actions/context-type/${query.contextType}/context-id/${query.contextId}`,
                { params }
            );
        }

        // For the base route, include contextType and contextId as query parameters if provided
        if (query.contextType) params = params.set('contextType', query.contextType.toString());
        if (query.contextId) params = params.set('contextId', query.contextId.toString());

        return this._http.get<Actions>('api/actions', { params });
    }

    getAction(query: GetActionQuery): Observable<Action> {
        return this._http.get<Action>(`api/actions/${query.id}`);
    }

    getActionMessage(query: GetActionMessageQuery): Observable<EmailLog> {
        return this._http.get<EmailLog>(`api/actions/${query.actionId}/message`);
    }

    getActionTypes(): Observable<ActionType[]> {
        return this._http.get<ActionType[]>('api/actions/types');
    }

    getOutstandingActionsCount(): Observable<OutstandingActionsCount> {
        return this._http.get<OutstandingActionsCount>('api/actions/me/outstanding-count');
    }

    getOutstandingActionsCountByContext(contextType: ContextType, contextId: number): Observable<OutstandingActionsCount> {
        return this._http.get<OutstandingActionsCount>(
            `api/actions/context-type/${contextType}/context-id/${contextId}/outstanding-count`
        );
    }
}
